import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import type { Database, Json } from '../generated';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---------- vendor (the row itself) -------------------------------------

export type Vendor = Database['vendors']['Tables']['vendors']['Row'];
export type VendorInsert = Database['vendors']['Tables']['vendors']['Insert'];
export type VendorUpdate = Database['vendors']['Tables']['vendors']['Update'];
export type VendorSummary = Database['vendors']['Views']['v_vendor_summary']['Row'];
export type VendorStatus = Database['vendors']['Enums']['vendor_status'];
export type VendorType = Database['vendors']['Enums']['vendor_type'];
export type VendorPaymentTerms = Database['vendors']['Enums']['payment_terms'];

export interface ListVendorsInput extends KeysetInput {
  search?: string;
  status?: VendorStatus;
  vendorType?: VendorType;
  tag?: string;
  preferredOnly?: boolean;
  approvedOnly?: boolean;
  includeArchived?: boolean;
  /**
   * Filter by the account-executive `assigned_user_id` column.
   * - A uuid string: vendors assigned to that specific user.
   * - `'unassigned'`: vendors whose `assigned_user_id IS NULL`.
   * - `'me'` is resolved to a uuid by the caller (we keep the service
   *   layer ignorant of the active session so it stays trivially
   *   testable).
   */
  assignedUserId?: string | 'unassigned';
}

export async function listVendors(
  ctx: ActiveContext,
  input: ListVendorsInput = {},
): Promise<ListResult<Vendor>> {
  const { orgId, storeId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = supabase()
    .schema('vendors')
    .from('vendors')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId)
    .eq('store_id', storeId);

  if (!input.includeArchived) q = q.neq('status', 'archived');
  if (input.status) q = q.eq('status', input.status);
  if (input.vendorType) q = q.eq('vendor_type', input.vendorType);
  if (input.preferredOnly) q = q.eq('preferred', true);
  if (input.approvedOnly) q = q.eq('approved', true);
  if (input.tag) q = q.contains('tags', [input.tag]);
  if (input.assignedUserId === 'unassigned') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    q = (q as any).is('assigned_user_id', null);
  } else if (input.assignedUserId) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    q = (q as any).eq('assigned_user_id', input.assignedUserId);
  }
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or(
      [
        `company_name.ilike.${s}`,
        `display_name.ilike.${s}`,
        `doing_business_as.ilike.${s}`,
        `email.ilike.${s}`,
        `phone.ilike.${s}`,
        `account_number.ilike.${s}`,
      ].join(','),
    );
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as Vendor[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function listVendorsTop(
  ctx: ActiveContext,
  input: Omit<ListVendorsInput, 'cursor' | 'countMode'> & { limit?: number } = {},
): Promise<Vendor[]> {
  const { rows } = await listVendors(ctx, {
    ...input,
    pageSize: input.limit ?? 25,
    countMode: 'none',
  });
  return rows;
}

export async function getVendor(ctx: ActiveContext, id: string): Promise<Vendor | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendors')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export interface CreateVendorInput {
  companyName: string;
  displayName?: string;
  doingBusinessAs?: string | null;
  vendorType?: VendorType;
  taxId?: string | null;
  w9OnFile?: boolean;
  w9ReceivedAt?: string | null;
  paymentTerms?: VendorPaymentTerms;
  defaultCurrency?: string;
  leadTimeDays?: number;
  minimumOrderCents?: number;
  accountNumber?: string | null;
  primaryContactName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  preferred?: boolean;
  approved?: boolean;
  notes?: string | null;
  tags?: string[];
  metadata?: Json;
  status?: VendorStatus;
}

function deriveDisplayName(
  input: CreateVendorInput | UpdateVendorInput,
): string | undefined {
  const trimmed = (v: string | null | undefined) => (v ?? '').trim();
  if (trimmed(input.displayName).length > 0) return trimmed(input.displayName);
  if (trimmed(input.doingBusinessAs).length > 0) return trimmed(input.doingBusinessAs);
  if (trimmed(input.companyName).length > 0) return trimmed(input.companyName);
  return undefined;
}

export async function createVendor(
  ctx: ActiveContext,
  input: CreateVendorInput,
): Promise<Vendor> {
  const { orgId, storeId } = requireContext(ctx);
  const company = (input.companyName ?? '').trim();
  if (!company) throw new Error('A vendor needs a company name.');
  const dn = deriveDisplayName(input) ?? company;
  const insert: VendorInsert = {
    org_id: orgId,
    store_id: storeId,
    company_name: company,
    display_name: dn,
    doing_business_as: input.doingBusinessAs ?? undefined,
    vendor_type: input.vendorType ?? 'other',
    tax_id: input.taxId ?? undefined,
    w9_on_file: Boolean(input.w9OnFile),
    w9_received_at: input.w9ReceivedAt ?? undefined,
    payment_terms: input.paymentTerms ?? 'net_30',
    default_currency: input.defaultCurrency ?? 'usd',
    lead_time_days: input.leadTimeDays ?? 0,
    minimum_order_cents: input.minimumOrderCents ?? 0,
    account_number: input.accountNumber ?? undefined,
    primary_contact_name: input.primaryContactName ?? undefined,
    email: input.email ?? undefined,
    phone: input.phone ?? undefined,
    website: input.website ?? undefined,
    preferred: Boolean(input.preferred),
    approved: Boolean(input.approved),
    approved_at: input.approved ? new Date().toISOString() : undefined,
    notes: input.notes ?? undefined,
    tags: input.tags ?? [],
    metadata: input.metadata ?? {},
    status: input.status ?? 'active',
  };
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendors')
    .insert(insert)
    .select('*')
    .single();
  if (error) throw error;
  return data as Vendor;
}

export interface UpdateVendorInput {
  id: string;
  companyName?: string;
  displayName?: string;
  doingBusinessAs?: string | null;
  vendorType?: VendorType;
  taxId?: string | null;
  w9OnFile?: boolean;
  w9ReceivedAt?: string | null;
  paymentTerms?: VendorPaymentTerms;
  defaultCurrency?: string;
  leadTimeDays?: number;
  minimumOrderCents?: number;
  accountNumber?: string | null;
  primaryContactName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  preferred?: boolean;
  approved?: boolean;
  notes?: string | null;
  tags?: string[];
  metadata?: Json;
  status?: VendorStatus;
  /**
   * Account-executive assignment. `null` clears the assignment;
   * `undefined` leaves it untouched. Org members continue to see all
   * org vendors via the existing tenant_isolation policy -- this is a
   * pure CRM filter, not an RLS gate.
   */
  assignedUserId?: string | null;
}

export async function updateVendor(
  ctx: ActiveContext,
  input: UpdateVendorInput,
): Promise<Vendor> {
  requireContext(ctx);
  const patch: VendorUpdate = {};
  const touchedName =
    input.displayName !== undefined ||
    input.companyName !== undefined ||
    input.doingBusinessAs !== undefined;
  if (touchedName) {
    const dn = deriveDisplayName(input);
    if (dn !== undefined) patch.display_name = dn;
  }
  if (input.companyName !== undefined) patch.company_name = input.companyName;
  if (input.doingBusinessAs !== undefined) patch.doing_business_as = input.doingBusinessAs;
  if (input.vendorType !== undefined) patch.vendor_type = input.vendorType;
  if (input.taxId !== undefined) patch.tax_id = input.taxId;
  if (input.w9OnFile !== undefined) patch.w9_on_file = input.w9OnFile;
  if (input.w9ReceivedAt !== undefined) patch.w9_received_at = input.w9ReceivedAt;
  if (input.paymentTerms !== undefined) patch.payment_terms = input.paymentTerms;
  if (input.defaultCurrency !== undefined) patch.default_currency = input.defaultCurrency;
  if (input.leadTimeDays !== undefined) patch.lead_time_days = input.leadTimeDays;
  if (input.minimumOrderCents !== undefined)
    patch.minimum_order_cents = input.minimumOrderCents;
  if (input.accountNumber !== undefined) patch.account_number = input.accountNumber;
  if (input.primaryContactName !== undefined)
    patch.primary_contact_name = input.primaryContactName;
  if (input.email !== undefined) patch.email = input.email;
  if (input.phone !== undefined) patch.phone = input.phone;
  if (input.website !== undefined) patch.website = input.website;
  if (input.preferred !== undefined) patch.preferred = input.preferred;
  if (input.approved !== undefined) {
    patch.approved = input.approved;
    patch.approved_at = input.approved ? new Date().toISOString() : null;
  }
  if (input.notes !== undefined) patch.notes = input.notes;
  if (input.tags !== undefined) patch.tags = input.tags;
  if (input.metadata !== undefined) patch.metadata = input.metadata;
  if (input.status !== undefined) patch.status = input.status;
  if (input.assignedUserId !== undefined) {
    (patch as VendorUpdate & { assigned_user_id?: string | null }).assigned_user_id =
      input.assignedUserId;
  }

  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendors')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as Vendor;
}

export async function archiveVendor(ctx: ActiveContext, id: string): Promise<void> {
  await updateVendor(ctx, { id, status: 'archived' });
}

export async function getVendorSummary(
  ctx: ActiveContext,
  id: string,
): Promise<VendorSummary | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('vendors')
    .from('v_vendor_summary')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

// ---------- contacts -----------------------------------------------------

export type VendorContact = Database['vendors']['Tables']['vendor_contacts']['Row'];
export type VendorContactInsert = Database['vendors']['Tables']['vendor_contacts']['Insert'];

export async function listVendorContacts(
  ctx: ActiveContext,
  vendorId: string,
): Promise<VendorContact[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_contacts')
    .select('*')
    .eq('vendor_id', vendorId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return data ?? [];
}

export interface UpsertVendorContactInput {
  id?: string;
  vendorId: string;
  name: string;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary?: boolean;
  notes?: string | null;
}

export async function createVendorContact(
  ctx: ActiveContext,
  input: UpsertVendorContactInput,
): Promise<VendorContact> {
  const { orgId, storeId } = requireContext(ctx);
  if (input.isPrimary) await clearPrimaryContact(ctx, input.vendorId);
  const row: VendorContactInsert = {
    org_id: orgId,
    store_id: storeId,
    vendor_id: input.vendorId,
    name: input.name,
    role: input.role ?? undefined,
    email: input.email ?? undefined,
    phone: input.phone ?? undefined,
    is_primary: Boolean(input.isPrimary),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_contacts')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as VendorContact;
}

export async function updateVendorContact(
  ctx: ActiveContext,
  input: UpsertVendorContactInput & { id: string },
): Promise<VendorContact> {
  requireContext(ctx);
  if (input.isPrimary) await clearPrimaryContact(ctx, input.vendorId, input.id);
  const patch: Partial<VendorContactInsert> = {
    name: input.name,
    role: input.role ?? undefined,
    email: input.email ?? undefined,
    phone: input.phone ?? undefined,
    is_primary: Boolean(input.isPrimary),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_contacts')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as VendorContact;
}

export async function deleteVendorContact(ctx: ActiveContext, id: string): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase()
    .schema('vendors')
    .from('vendor_contacts')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

async function clearPrimaryContact(
  ctx: ActiveContext,
  vendorId: string,
  exceptId?: string,
) {
  requireContext(ctx);
  let q = supabase()
    .schema('vendors')
    .from('vendor_contacts')
    .update({ is_primary: false })
    .eq('vendor_id', vendorId)
    .eq('is_primary', true);
  if (exceptId) q = q.neq('id', exceptId);
  const { error } = await q;
  if (error) throw error;
}

// ---------- addresses ----------------------------------------------------

export type VendorAddress = Database['vendors']['Tables']['vendor_addresses']['Row'];
export type VendorAddressInsert =
  Database['vendors']['Tables']['vendor_addresses']['Insert'];

export async function listVendorAddresses(
  ctx: ActiveContext,
  vendorId: string,
): Promise<VendorAddress[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_addresses')
    .select('*')
    .eq('vendor_id', vendorId)
    .order('is_default_ship_from', { ascending: false })
    .order('is_default_remit_to', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(100);
  if (error) throw error;
  return data ?? [];
}

export interface UpsertVendorAddressInput {
  id?: string;
  vendorId: string;
  label?: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
  isDefaultRemitTo?: boolean;
  isDefaultShipFrom?: boolean;
  notes?: string | null;
}

export async function createVendorAddress(
  ctx: ActiveContext,
  input: UpsertVendorAddressInput,
): Promise<VendorAddress> {
  const { orgId, storeId } = requireContext(ctx);
  if (input.isDefaultRemitTo) await clearDefaultAddress(ctx, input.vendorId, 'remit_to');
  if (input.isDefaultShipFrom) await clearDefaultAddress(ctx, input.vendorId, 'ship_from');

  const row: VendorAddressInsert = {
    org_id: orgId,
    store_id: storeId,
    vendor_id: input.vendorId,
    label: input.label ?? 'primary',
    address_line1: input.addressLine1,
    address_line2: input.addressLine2 ?? undefined,
    city: input.city,
    state: input.state,
    postal_code: input.postalCode,
    country: input.country ?? 'US',
    is_default_remit_to: Boolean(input.isDefaultRemitTo),
    is_default_ship_from: Boolean(input.isDefaultShipFrom),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_addresses')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as VendorAddress;
}

export async function updateVendorAddress(
  ctx: ActiveContext,
  input: UpsertVendorAddressInput & { id: string },
): Promise<VendorAddress> {
  requireContext(ctx);
  if (input.isDefaultRemitTo)
    await clearDefaultAddress(ctx, input.vendorId, 'remit_to', input.id);
  if (input.isDefaultShipFrom)
    await clearDefaultAddress(ctx, input.vendorId, 'ship_from', input.id);
  const patch: Partial<VendorAddressInsert> = {
    label: input.label ?? 'primary',
    address_line1: input.addressLine1,
    address_line2: input.addressLine2 ?? undefined,
    city: input.city,
    state: input.state,
    postal_code: input.postalCode,
    country: input.country ?? 'US',
    is_default_remit_to: Boolean(input.isDefaultRemitTo),
    is_default_ship_from: Boolean(input.isDefaultShipFrom),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_addresses')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as VendorAddress;
}

export async function deleteVendorAddress(ctx: ActiveContext, id: string): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase()
    .schema('vendors')
    .from('vendor_addresses')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

async function clearDefaultAddress(
  ctx: ActiveContext,
  vendorId: string,
  kind: 'remit_to' | 'ship_from',
  exceptId?: string,
) {
  requireContext(ctx);
  const patch =
    kind === 'remit_to'
      ? { is_default_remit_to: false }
      : { is_default_ship_from: false };
  const col = kind === 'remit_to' ? 'is_default_remit_to' : 'is_default_ship_from';
  let q = supabase()
    .schema('vendors')
    .from('vendor_addresses')
    .update(patch)
    .eq('vendor_id', vendorId)
    .eq(col, true);
  if (exceptId) q = q.neq('id', exceptId);
  const { error } = await q;
  if (error) throw error;
}

// ---------- SKU links ----------------------------------------------------

export type VendorSkuLink = Database['vendors']['Tables']['vendor_sku_links']['Row'];
export type VendorSkuLinkInsert =
  Database['vendors']['Tables']['vendor_sku_links']['Insert'];

export async function listVendorSkuLinks(
  ctx: ActiveContext,
  vendorId: string,
): Promise<VendorSkuLink[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_sku_links')
    .select('*')
    .eq('vendor_id', vendorId)
    .order('is_preferred', { ascending: false })
    .order('vendor_part_number', { ascending: true })
    .limit(1000);
  if (error) throw error;
  return data ?? [];
}

export interface UpsertVendorSkuLinkInput {
  id?: string;
  vendorId: string;
  inventorySkuId?: string | null;
  vendorPartNumber?: string | null;
  description?: string | null;
  unitCostCents?: number | null;
  unitOfMeasure?: string | null;
  packSize?: number | null;
  isPreferred?: boolean;
  leadTimeDays?: number | null;
  notes?: string | null;
}

export async function createVendorSkuLink(
  ctx: ActiveContext,
  input: UpsertVendorSkuLinkInput,
): Promise<VendorSkuLink> {
  const { orgId, storeId } = requireContext(ctx);
  const row: VendorSkuLinkInsert = {
    org_id: orgId,
    store_id: storeId,
    vendor_id: input.vendorId,
    inventory_sku_id: input.inventorySkuId ?? undefined,
    vendor_part_number: input.vendorPartNumber ?? undefined,
    description: input.description ?? undefined,
    unit_cost_cents: input.unitCostCents ?? undefined,
    unit_of_measure: input.unitOfMeasure ?? undefined,
    pack_size: input.packSize ?? undefined,
    is_preferred: Boolean(input.isPreferred),
    lead_time_days: input.leadTimeDays ?? undefined,
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_sku_links')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as VendorSkuLink;
}

export async function updateVendorSkuLink(
  ctx: ActiveContext,
  input: UpsertVendorSkuLinkInput & { id: string },
): Promise<VendorSkuLink> {
  requireContext(ctx);
  const patch: Partial<VendorSkuLinkInsert> = {
    inventory_sku_id: input.inventorySkuId ?? undefined,
    vendor_part_number: input.vendorPartNumber ?? undefined,
    description: input.description ?? undefined,
    unit_cost_cents: input.unitCostCents ?? undefined,
    unit_of_measure: input.unitOfMeasure ?? undefined,
    pack_size: input.packSize ?? undefined,
    is_preferred: Boolean(input.isPreferred),
    lead_time_days: input.leadTimeDays ?? undefined,
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('vendors')
    .from('vendor_sku_links')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as VendorSkuLink;
}

export async function deleteVendorSkuLink(ctx: ActiveContext, id: string): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase()
    .schema('vendors')
    .from('vendor_sku_links')
    .delete()
    .eq('id', id);
  if (error) throw error;
}
